import type { CSSProperties, ReactNode } from 'react';
import { ChorePriority } from '@chore-project/shared';
import { SwipeableRow } from './SwipeableRow';

/** Check-ring colour per priority. SHOULD_DO (the default) stays brand
 *  terracotta; must/can lean on the shared priority tokens. */
const RING_COLOR: Record<ChorePriority, string> = {
  [ChorePriority.MUST_DO]: 'var(--priority-must)',
  [ChorePriority.SHOULD_DO]: 'var(--primary)',
  [ChorePriority.CAN_DO]: 'var(--priority-can)',
};

interface ChoreTileProps {
  title: string;
  /** Tints the check-circle ring. */
  priority?: ChorePriority;
  /** Small-caps room label on its own line above the tile foot. */
  room?: string;
  /** Foot metadata — stacked lines beside the check/state icon. */
  meta?: ReactNode;
  /** Bare points value shown in the tile's top-right corner. */
  points?: number;
  /** ionicon name for a passive disc instead of the check-circle
   *  (tiles that can't be completed here: incoming passes, waiting, skipped). */
  icon?: string;
  /** Buttons in a row across the tile foot (Accept/Decline, Cancel). */
  actions?: ReactNode;
  /** Completed styling: green tint, filled check, struck-through title. */
  done?: boolean;
  /** Mid-completion: reads as done while the mutation lands. */
  completing?: boolean;
  disabled?: boolean;
  /** Red-tinted tile for overdue chores. */
  overdue?: boolean;
  /** Tap on the check-circle (and swipe right). */
  onComplete?: () => void;
  /** Swipe left. */
  onSkip?: () => void;
  /** Tap anywhere else on the tile — opens the chore sheet. */
  onOpen?: () => void;
}

/**
 * The chore tile: a chunky grid cell with the points value bare in the top
 * corner, display-type title, room in small caps, and length/due stacked
 * beside a big tap-to-complete check-circle (or a passive state disc for
 * incoming/waiting/skipped chores, with any action buttons in a row across
 * the foot). Wraps itself in SwipeableRow when swipe actions apply.
 */
export function ChoreTile({
  title,
  priority = ChorePriority.SHOULD_DO,
  room,
  meta,
  points,
  icon,
  actions,
  done = false,
  completing = false,
  disabled = false,
  overdue = false,
  onComplete,
  onSkip,
  onOpen,
}: ChoreTileProps) {
  const showDone = done || completing;
  const showCheck = !!onComplete || done;

  const check = showCheck ? (
    <button
      className={`chore-check${showDone ? ' chore-check-done' : ''}`}
      style={{ '--check-ring': RING_COLOR[priority] } as CSSProperties}
      onClick={onComplete}
      disabled={disabled || done || !onComplete}
      aria-label={done ? `${title} — completed` : `Mark ${title} done`}
    >
      <ion-icon name="checkmark-sharp" />
    </button>
  ) : icon ? (
    <span className="chore-check chore-check-passive" aria-hidden="true">
      <ion-icon name={icon} />
    </span>
  ) : null;

  const tileClass =
    `chore-tile${overdue && !showDone ? ' chore-tile--late' : ''}` +
    `${showDone ? ' chore-tile--done' : ''}`;

  const inner = (
    <div className="chore-tile-inner">
      {points != null && <span className="chore-tile-pts">+{points}</span>}
      {onOpen ? (
        <button className="chore-tile-open" onClick={onOpen} disabled={disabled}>
          <span className="chore-title">{title}</span>
        </button>
      ) : (
        <span className="chore-title">{title}</span>
      )}
      {room && <span className="chore-room chore-tile-room">{room}</span>}
      <div className="chore-tile-foot">
        <span className="chore-tile-meta">{meta}</span>
        {check}
      </div>
      {actions && <div className="chore-tile-actions">{actions}</div>}
    </div>
  );

  if (onComplete || onSkip) {
    return (
      <SwipeableRow className={tileClass} onComplete={onComplete} onSkip={onSkip} disabled={disabled || completing}>
        {inner}
      </SwipeableRow>
    );
  }
  return <div className={tileClass}>{inner}</div>;
}
